import { BadgePanel, HonestyPopover, type PanelRow } from './Popover'
import { DOT_TRIGGER } from './chrome'
import { t } from '@/lib/ui'

// Automation level per step (DESIGN-BRIEF §4 / SIM-015 §2). Four marks a viewer
// must be able to tell apart in three seconds:
//   auto       — filled petrol dot         (fully automated; hallintolaki 53 e §)
//   assisted   — half-filled amber dot      (automation prepares, human decides)
//   human-law  — slate-blue ring            (human because the law reserves it — permanent)
//   human-gap  — grey dashed ring           (human because a data gap routes it there — fixable)
//
// The last distinction (SIM-015's data-gap vs genuine discretion) is the one
// that keeps the mock from overstating how much is irreducibly manual.

export type AutomationLevel = 'auto' | 'assisted' | 'human-law' | 'human-gap'

interface AutomationDotProps {
  level: AutomationLevel
  /** Statutory reason (human-law) or the 53 e/g § disclosure (auto). */
  statute?: string
  /** The citizen's path to a person (required on assisted/human by hallintolaki 53 f §). */
  humanRoute?: string
  /** Overrides the generic explanation. */
  note?: string
  /** Deep-links to /spec/:id (usually the governing AI spec). */
  specId?: string
  /** Render the level name beside the dot (used in legends / walk steps). */
  showLabel?: boolean
}

const NAME_KEY = {
  auto: 'honesty.auto.name',
  assisted: 'honesty.assisted.name',
  'human-law': 'honesty.humanLaw.name',
  'human-gap': 'honesty.humanGap.name',
} as const

const HINT_KEY = {
  auto: 'honesty.auto.hint',
  assisted: 'honesty.assisted.hint',
  'human-law': 'honesty.humanLaw.hint',
  'human-gap': 'honesty.humanGap.hint',
} as const

/** The 12×12 mark for each level. Colors come from the mirrored CSS tokens. */
function DotMark({ level }: { level: AutomationLevel }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="shrink-0">
      {level === 'auto' && <circle cx="6" cy="6" r="5" fill="var(--accent)" />}
      {level === 'assisted' && (
        <>
          <circle cx="6" cy="6" r="5" fill="none" stroke="var(--design)" strokeWidth="1.5" />
          {/* left half filled — automation prepares, human decides */}
          <path d="M6,1 A5,5 0 0,0 6,11 Z" fill="var(--design)" />
        </>
      )}
      {level === 'human-law' && (
        <circle cx="6" cy="6" r="5" fill="none" stroke="var(--law)" strokeWidth="1.5" />
      )}
      {level === 'human-gap' && (
        <circle
          cx="6"
          cy="6"
          r="5"
          fill="none"
          stroke="var(--muted)"
          strokeWidth="1.5"
          strokeDasharray="2 2"
        />
      )}
    </svg>
  )
}

export function AutomationDot({
  level,
  statute,
  humanRoute,
  note,
  specId,
  showLabel = false,
}: AutomationDotProps) {
  const name = t(NAME_KEY[level])
  const rows: PanelRow[] = []
  if (statute) {
    const label = level === 'auto' ? t('honesty.popover.disclosureLabel') : t('honesty.popover.sourceLabel')
    rows.push({ label, value: statute })
  }
  if (humanRoute) rows.push({ label: t('honesty.popover.routeLabel'), value: humanRoute })

  return (
    <HonestyPopover
      triggerAriaLabel={name}
      triggerClassName={DOT_TRIGGER}
      triggerLabel={
        <span className="inline-flex items-center gap-1.5">
          <DotMark level={level} />
          {showLabel && <span className="text-xs font-medium text-ink">{name}</span>}
        </span>
      }
    >
      <BadgePanel name={name} hint={note ?? t(HINT_KEY[level])} rows={rows} specId={specId} />
    </HonestyPopover>
  )
}
